// The map: a corner minimap around you and a whole-island map on N, both under a fog of war.
// The client regenerates the island from the world seed (shared/worldgen.ts) for the base picture;
// the server owns the explored mask (sent once on connect) and the client mirrors it as you move.
import { GROUNDS, MAP_H, MAP_W, OBJECTS, TILE } from '../../shared/content.ts';
import { Settings } from './settings.ts';
import { EXPLORE_CELL, EXPLORE_H, EXPLORE_W, decodeExplored, isExplored, markExplored, newExplored } from '../../shared/explore.ts';
import { generate, type WorldMap } from '../../shared/worldgen.ts';

type Claim = { x: number; y: number; w: number; h: number; owner: string; mine: boolean };
type Dot = { x: number; y: number; me: boolean; name: string };

const MINI = 200; // minimap size in CSS pixels
const MINI_TILES = 100; // tiles across the minimap
const FOG = '#0d0f0c';

export class Minimap {
  private map: WorldMap | null = null;
  private base = document.createElement('canvas');
  private fog = document.createElement('canvas');
  private explored = newExplored();
  private mini: HTMLCanvasElement;
  private big: HTMLCanvasElement;
  private bigWrap: HTMLElement;
  private claims: Claim[] = [];
  private dots: Dot[] = [];
  private drawnAt = 0;

  constructor() {
    this.mini = document.createElement('canvas');
    this.mini.id = 'minimap';
    this.mini.title = 'Map (N)';
    this.mini.width = this.mini.height = MINI * devicePixelRatio;
    this.mini.onclick = () => this.toggle();
    Settings.watch((s) => (this.mini.style.display = s.showMinimap ? '' : 'none'));
    this.bigWrap = document.createElement('div');
    this.bigWrap.id = 'bigmap';
    this.bigWrap.className = 'hidden';
    this.big = document.createElement('canvas');
    this.bigWrap.append(this.big);
    const hint = document.createElement('p');
    hint.textContent = 'N or Esc to close · gold: your ruins · blue: other claims · blue dots: dungeon portals · violet: realm portals';
    this.bigWrap.append(hint);
    this.bigWrap.onclick = () => this.toggle(false);
    document.getElementById('hud')!.append(this.mini, this.bigWrap);
    this.fog.width = EXPLORE_W;
    this.fog.height = EXPLORE_H;
    document.addEventListener('keydown', (e) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      if (e.code === 'KeyN') this.toggle();
      else if (e.key === 'Escape') this.toggle(false);
    });
  }

  get open(): boolean {
    return !this.bigWrap.classList.contains('hidden');
  }

  /** Build the base picture from the seed (about a third of a second, once). */
  setSeed(seed: number): void {
    if (this.map?.seed === seed) return;
    setTimeout(() => {
      this.map = generate(seed);
      this.paintBase();
      this.drawnAt = 0;
    }, 50);
  }

  setExplored(b64: string): void {
    this.explored = decodeExplored(b64);
    this.paintFog();
  }

  setClaims(list: Claim[]): void {
    this.claims = list;
  }

  /** Called every frame with where everyone is (pixels). Redraws a few times a second. */
  update(now: number, me: { x: number; y: number } | null, others: Dot[]): void {
    if (me && markExplored(this.explored, me.x, me.y)) this.paintFog();
    this.dots = others;
    if (now - this.drawnAt < 150 || !me) return;
    this.drawnAt = now;
    this.drawMini(me.x / TILE, me.y / TILE);
    if (this.open) this.drawBig();
  }

  toggle(show = !this.open): void {
    this.bigWrap.classList.toggle('hidden', !show);
    if (show) this.drawBig();
  }

  // ---------------------------------------------------------------- painting

  private paintBase(): void {
    const map = this.map!;
    this.base.width = MAP_W;
    this.base.height = MAP_H;
    const ctx = this.base.getContext('2d')!;
    const img = ctx.createImageData(MAP_W, MAP_H);
    for (let i = 0; i < MAP_W * MAP_H; i++) {
      const o = map.obj[i];
      const g = GROUNDS[map.ground[i]].color;
      let c = g;
      const od = o ? OBJECTS[o] : null;
      // Trees and rock darken the ground a little rather than speckling it; ruins and ore show as is.
      if (od && (od.kind === 'tree' || od.kind === 'rock' || od.kind === 'bush')) c = mix(g, od.color, 0.45);
      else if (od && od.kind !== 'decor' && od.kind !== 'stump' && od.kind !== 'crop') c = od.color;
      img.data[i * 4] = c >> 16;
      img.data[i * 4 + 1] = (c >> 8) & 255;
      img.data[i * 4 + 2] = c & 255;
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
  }

  private paintFog(): void {
    const ctx = this.fog.getContext('2d')!;
    const img = ctx.createImageData(EXPLORE_W, EXPLORE_H);
    for (let cy = 0; cy < EXPLORE_H; cy++) {
      for (let cx = 0; cx < EXPLORE_W; cx++) {
        const k = (cy * EXPLORE_W + cx) * 4;
        img.data[k] = 13;
        img.data[k + 1] = 15;
        img.data[k + 2] = 12;
        img.data[k + 3] = isExplored(this.explored, cx, cy) ? 0 : 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  }

  /** Draw the map's area (tiles) [x0, y0, size] into a square of `px` pixels. */
  private paint(ctx: CanvasRenderingContext2D, x0: number, y0: number, size: number, px: number): void {
    ctx.fillStyle = FOG;
    ctx.fillRect(0, 0, px, px);
    if (!this.map) return;
    const k = px / size;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(this.base, -x0 * k, -y0 * k, MAP_W * k, MAP_H * k);
    ctx.imageSmoothingEnabled = true; // soft fog edges
    ctx.drawImage(this.fog, -x0 * k, -y0 * k, MAP_W * k, MAP_H * k);
    const at = (tx: number, ty: number): [number, number] => [(tx - x0) * k, (ty - y0) * k];
    const seen = (tx: number, ty: number) => isExplored(this.explored, Math.floor(tx / EXPLORE_CELL), Math.floor(ty / EXPLORE_CELL));
    for (const c of this.claims) {
      const [x, y] = at(c.x, c.y);
      ctx.strokeStyle = c.mine ? '#f0c040' : '#6aa0ff';
      ctx.lineWidth = Math.max(1.5, k * 0.8);
      ctx.strokeRect(x, y, c.w * k, c.h * k);
    }
    for (const d of this.map.dungeons) {
      for (const p of [d.portal, d.exit]) {
        if (!seen(p.tx, p.ty)) continue;
        const [x, y] = at(p.tx + 0.5, p.ty + 0.5);
        // Realm portals are violet; dungeon portals blue.
        ctx.fillStyle = d.realm ? '#b070ff' : '#5aa0ff';
        ctx.strokeStyle = d.realm ? '#ecd8ff' : '#d0e4ff';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(x, y, Math.max(3, k * 1.2), 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        if (px > 400 && p === d.portal) label(ctx, d.name, x, y - 10, '#cfe0ff');
      }
    }
    for (const d of this.dots) {
      if (d.me) continue;
      const [x, y] = at(d.x / TILE, d.y / TILE);
      ctx.fillStyle = '#9ad0ff';
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
      if (px > 400) label(ctx, d.name, x, y - 7, '#cfe0ff');
    }
    const me = this.dots.find((d) => d.me);
    if (me) {
      const [x, y] = at(me.x / TILE, me.y / TILE);
      ctx.fillStyle = '#fff4c0';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fill();
    }
  }

  private drawMini(tx: number, ty: number): void {
    const ctx = this.mini.getContext('2d')!;
    ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    this.paint(ctx, tx - MINI_TILES / 2, ty - MINI_TILES / 2, MINI_TILES, MINI);
  }

  private drawBig(): void {
    const px = Math.max(300, Math.min(window.innerWidth, window.innerHeight) - 90);
    const dpr = devicePixelRatio;
    if (this.big.width !== px * dpr) {
      this.big.width = this.big.height = px * dpr;
      this.big.style.width = this.big.style.height = `${px}px`;
    }
    const ctx = this.big.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.paint(ctx, 0, 0, MAP_W, px);
  }
}

function mix(a: number, b: number, t: number): number {
  const ch = (s: number) => Math.round(((a >> s) & 255) * (1 - t) + ((b >> s) & 255) * t);
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

function label(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, color: string): void {
  ctx.font = '12px Georgia, serif';
  ctx.textAlign = 'center';
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#000';
  ctx.strokeText(text, x, y);
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
}
